import { useState, useMemo } from 'react';
import { Contact } from '@/hooks/useContacts';

export interface ContactFilterState {
  search: string;
  contactTypes: string[];
  statuses: string[];
  tags: string[];
}

const initialFilterState: ContactFilterState = {
  search: '',
  contactTypes: [],
  statuses: [],
  tags: [],
};

export function useContactFilters() {
  const [filters, setFilters] = useState<ContactFilterState>(initialFilterState);

  const updateFilters = (updates: Partial<ContactFilterState>) => {
    setFilters(prev => ({ ...prev, ...updates }));
  };

  const clearFilters = () => {
    setFilters(initialFilterState);
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search.trim()) count++;
    count += filters.contactTypes.length;
    count += filters.statuses.length;
    count += filters.tags.length;
    return count;
  }, [filters]);

  const applyFilters = (contacts: Contact[]): Contact[] => {
    return contacts.filter(contact => {
      // Search filter (name, email, phone, company)
      if (filters.search.trim()) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = 
          contact.name.toLowerCase().includes(searchLower) ||
          contact.email?.toLowerCase().includes(searchLower) ||
          contact.phone?.toLowerCase().includes(searchLower) ||
          contact.company?.name?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Contact type filter (OR logic)
      if (filters.contactTypes.length > 0) {
        if (!filters.contactTypes.includes(contact.contact_type)) {
          return false;
        }
      }

      // Status filter (OR logic)
      if (filters.statuses.length > 0) {
        if (!filters.statuses.includes(contact.status)) {
          return false;
        }
      }

      // Tags filter (AND logic - contact must have ALL selected tags)
      if (filters.tags.length > 0) {
        const contactTags = contact.tags || [];
        const hasAllTags = filters.tags.every(tag => 
          contactTags.includes(tag)
        );
        if (!hasAllTags) return false;
      }

      return true;
    });
  };

  return {
    filters,
    updateFilters,
    clearFilters,
    activeFilterCount,
    applyFilters,
  };
}
